import { LDFlagSet } from 'launchdarkly-react-client-sdk';
import { memoize } from 'micro-memoize';

import { CreateNewForecastPayload } from '@futrli/predict/forecast/dashboard/types';
import { CurrentUserCheck } from '@futrli/shared-services/authenticator/auth0/types';
import { Organisation } from '@futrli-sdk/organisations';
import { Forecast } from '@futrli-sdk/predict';

import { ApiView, ApiViewState, getDefaultApiViewState } from '../../../core/ApiView';
import { createForecastMutation, deleteForecastMutation, updateForecastMutation } from '../../../mutations/forecasts';
import { currentUserQuery } from '../../../queries/currentUser';
import { ExtendedForecastListItem, processForecasts } from '../../../processors/forecasts/forecastsList';
import { forecastsQuery } from '../../../queries/forecasts';
import { membersQuery } from '../../../queries/members';
import { organisationQuery } from '../../../queries/organisations';

export interface ForecastsViewState extends ApiViewState {
  currentUser?: CurrentUserCheck;
  datasourceModifiedDate?: string;
  errorCreating: Error | null;
  errorDeleting: Error | null;
  errorUpdating: Error | null;
  forecasts?: ExtendedForecastListItem[];
  isCreating: boolean;
  isDeleting: boolean;
  isUpdating: boolean;
  newForecastId?: string;
  organisationCreated?: Organisation['created'];
}

export class ForecastsView extends ApiView {
  private currentUserObs: ReturnType<typeof currentUserQuery>;
  private forecastsObs: ReturnType<typeof forecastsQuery>;
  private membersObs: ReturnType<typeof membersQuery>;
  private organisationObs: ReturnType<typeof organisationQuery>;

  private createMutation: ReturnType<typeof createForecastMutation>;
  private deleteMutation: ReturnType<typeof deleteForecastMutation>;
  private updateMutation: ReturnType<typeof updateForecastMutation>;

  private memoizedProcessForecasts: typeof processForecasts;

  public state: ForecastsViewState = {
    ...getDefaultApiViewState(),
    currentUser: undefined,
    datasourceModifiedDate: undefined,
    errorCreating: null,
    errorDeleting: null,
    errorUpdating: null,
    forecasts: [],
    isCreating: false,
    isDeleting: false,
    isUpdating: false,
    newForecastId: undefined,
    organisationCreated: undefined
  };

  constructor(private workspaceId: string, private organisationId: string, private flags: LDFlagSet = {}) {
    super();

    // Observers
    this.currentUserObs = this.addQuery(currentUserQuery());
    this.forecastsObs = this.addQuery(forecastsQuery(workspaceId, organisationId));
    this.membersObs = this.addQuery(membersQuery(workspaceId, organisationId, false));
    this.organisationObs = this.addQuery(organisationQuery(workspaceId, organisationId));

    // Mutations
    this.createMutation = this.addMutation(createForecastMutation(workspaceId, organisationId));
    this.deleteMutation = this.addMutation(deleteForecastMutation(workspaceId, organisationId));
    this.updateMutation = this.addMutation(updateForecastMutation(workspaceId, organisationId));

    this.memoizedProcessForecasts = memoize(processForecasts);

    this.updateState();
  }

  // ***************
  // *  Public API *
  // ***************
  public createForecast(data: CreateNewForecastPayload) {
    return this.createMutation.mutate(data);
  }

  public deleteForecast(forecastId: string) {
    return this.deleteMutation.mutate(forecastId);
  }

  public lockForecast(forecastId: string) {
    return this.updateMutation.mutate({ forecastId, data: { is_frozen: true } });
  }

  public publishForecast(forecastId: string) {
    return this.updateMutation.mutate({ forecastId, data: { status: 'published' } });
  }

  public resetMutations() {
    this.createMutation.reset();
    this.deleteMutation.reset();
    this.updateMutation.reset();
    this.updateState();
  }

  public unpublishForecast(forecastId: string) {
    return this.updateMutation.mutate({ forecastId, data: { status: 'draft' } });
  }

  public updateForecast(forecastId: string, data: Partial<Forecast>) {
    return this.updateMutation.mutate({ forecastId, data });
  }

  // ****************************
  // *  Private / Protected API *
  // ****************************
  private get currentUser() {
    return this.getResult(this.currentUserObs).data;
  }

  private get datasourceModifiedDate() {
    return this.getResult(this.organisationObs).data?.datasource?.status.modified;
  }

  private get organisationCreated() {
    return this.getResult(this.organisationObs).data?.created;
  }

  private get errorCreating() {
    return this.createMutation.getCurrentResult().error;
  }

  private get errorDeleting() {
    return this.deleteMutation.getCurrentResult().error;
  }

  private get errorUpdating() {
    return this.updateMutation.getCurrentResult().error;
  }

  private get forecasts() {
    const rawForecasts = this.getResult(this.forecastsObs).data ?? [];
    const currentUser = this.currentUser;
    const members = this.members;
    if (!currentUser || !rawForecasts || !members) {
      return undefined;
    }
    return this.memoizedProcessForecasts(rawForecasts, currentUser, members, this.flags);
  }

  private get members() {
    return this.getResult(this.membersObs).data;
  }

  protected getNewState(): ForecastsViewState {
    return {
      ...super.getNewState(),
      currentUser: this.currentUser,
      datasourceModifiedDate: this.datasourceModifiedDate,
      errorCreating: this.errorCreating,
      errorDeleting: this.errorDeleting,
      errorUpdating: this.errorUpdating,
      forecasts: this.forecasts,
      isCreating: this.isCreating,
      isDeleting: this.isDeleting,
      isUpdating: this.isUpdating,
      newForecastId: this.newForecastId,
      organisationCreated: this.organisationCreated
    };
  }

  private get isCreating() {
    return this.createMutation.getCurrentResult().status === 'pending';
  }

  private get isDeleting() {
    return this.deleteMutation.getCurrentResult().status === 'pending';
  }

  private get isUpdating() {
    return this.updateMutation.getCurrentResult().status === 'pending';
  }

  private get newForecastId() {
    return this.createMutation.getCurrentResult().data?.id;
  }
}
